import React, { useEffect, useState } from 'react';
import { BRAND, PLANS, formatPrice } from '../shared/config';
import { isWellFormedLicenseKey } from '../shared/license';
import { translations } from '../shared/i18n';
import type { Language } from '../types';
import { activateLicense, getWebDeviceId } from '../services/security';

interface Props {
  lang: Language;
  onActivated: () => void;
  onClose: () => void;
}

const ActivationOverlay: React.FC<Props> = ({ lang, onActivated, onClose }) => {
  const t = translations[lang];
  const [deviceId, setDeviceId] = useState('');
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { void getWebDeviceId().then((id) => setDeviceId(id.slice(0, 8))); }, []);

  // Escape closes, as any dialog should.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /** Reformat as they type so a licence key cannot be entered subtly wrong. */
  const handleChange = (raw: string) => {
    const body = raw.toUpperCase().replace(/[^0-9A-Z]/g, '').replace(/^QDP/, '').slice(0, 15);
    const groups = body.match(/.{1,5}/g) ?? [];
    setKey(groups.length ? `QDP-${groups.join('-')}` : '');
    setError(null);
  };

  const handleActivate = async () => {
    setLoading(true);
    setError(null);
    const result = await activateLicense(key);
    if (result.ok) { onActivated(); return; }
    setLoading(false);
    setError(result.reason === 'offline' ? t.activationOffline : t.activationBadKey);

  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t.activation}
      className="fixed inset-0 z-[1000] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl p-7 shadow-2xl max-w-sm w-full my-8 qd-enter"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900">{t.activation}</h2>
            <p className="text-slate-500 text-xs mt-1">{t.upgradeBlurb}</p>
          </div>
          <button
            onClick={onClose}
            aria-label={t.close}
            className="w-9 h-9 -mt-1 -me-1 rounded-full text-slate-400 hover:bg-slate-100 shrink-0"
          >
            ✕
          </button>
        </div>

        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">
          {t.licenseKey}
        </label>
        <input
          type="text"
          value={key}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && isWellFormedLicenseKey(key)) void handleActivate(); }}
          placeholder="QDP-XXXXX-XXXXX-XXXXX"
          maxLength={23}
          autoCapitalize="characters"
          spellCheck={false}
          className={`w-full bg-slate-50 border p-4 rounded-xl text-center font-mono text-base font-extrabold tracking-widest outline-none transition-colors ${
            error ? 'border-red-400 animate-shake' : 'border-slate-200 focus:border-[#C1272D]'
          }`}
        />
        {error && <p role="alert" className="text-red-600 text-xs mt-2 font-bold">{error}</p>}

        <button
          onClick={handleActivate}
          disabled={loading || !isWellFormedLicenseKey(key)}
          className="w-full bg-[#C1272D] text-white py-4 rounded-xl font-extrabold mt-4 disabled:opacity-40 transition-opacity"
        >
          {loading ? t.activating : t.activateApp}
        </button>

        <div className="mt-6 pt-5 border-t border-slate-100">
          <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">
            {t.buyLicense}
          </p>
          <div className="space-y-1.5 mb-4">
            {(['annual', 'monthly', 'lifetime'] as const).map((id) => (
              <div key={id} className="flex justify-between text-xs">
                <span className="font-bold text-slate-600 capitalize">{id}</span>
                <span className="font-extrabold text-slate-900">{formatPrice(PLANS[id].amount)}</span>
              </div>
            ))}
          </div>
          <a
            href={`${BRAND.siteUrl}/#pricing`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center bg-slate-100 text-slate-700 py-3 rounded-xl font-bold text-sm hover:bg-slate-200 transition-colors"
          >
            {BRAND.domain}
          </a>
        </div>

        <div className="mt-5 flex justify-between items-center text-[10px] text-slate-400">
          <span>{t.deviceId}</span>
          <span className="font-mono font-bold text-slate-600">{deviceId}</span>
        </div>
      </div>
    </div>
  );
};

export default ActivationOverlay;
